import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ExpenseSummary,
  ClientApprovalStatus,
  ClientTask,
  CreateClientTaskInput,
  UpdateClientTaskInput,
  ClientExpensePaymentMethods,
} from '@peles/shared';
import * as portalApi from '../api/portal';
import { useAuth } from '../context/AuthContext';
import type {
  ClientEvent,
  ClientEventDetail,
  ClientExpenseItem,
  ClientExpenseDocument,
  ClientGuest,
  ClientFloorPlan,
  ClientPhoto,
  ClientTimeline,
  ClientQuestionnaire,
  VendorEvent,
  VendorExpenseItem,
  VendorTimelineEntry,
  VendorFloorPlan,
  VendorProfile,
  PortalTaskScope,
} from '../api/portal';

// ─── Client Portal ─────────────────────────────────────────────────────────

export function useClientEvents() {
  return useQuery<ClientEvent[]>({
    queryKey: ['portal', 'client', 'events'],
    queryFn: () => portalApi.getClientEvents(),
  });
}

export function useClientExpenseSummary(eventId: string) {
  return useQuery<ExpenseSummary>({
    queryKey: ['portal', 'client', 'expense-summary', eventId],
    queryFn: () => portalApi.getClientExpenseSummary(eventId),
    enabled: !!eventId,
  });
}

export function useClientExpenseItems(eventId: string) {
  return useQuery<ClientExpenseItem[]>({
    queryKey: ['portal', 'client', 'expenses', eventId],
    queryFn: () => portalApi.getClientExpenseItems(eventId),
    enabled: !!eventId,
  });
}

/** Read-only files attached to one of the client's event expenses. */
export function useClientExpenseDocuments(eventId: string, expenseItemId: string | null) {
  return useQuery<ClientExpenseDocument[]>({
    queryKey: ['portal', 'client', 'expense-documents', eventId, expenseItemId],
    queryFn: () => portalApi.getClientExpenseDocuments(eventId, expenseItemId!),
    enabled: !!eventId && !!expenseItemId,
  });
}

/** How the client pays one of their event's expense lines. */
export function useClientExpensePaymentMethods(eventId: string, expenseItemId: string | null) {
  return useQuery<ClientExpensePaymentMethods>({
    queryKey: ['portal', 'client', 'expense-payment-methods', eventId, expenseItemId],
    queryFn: () => portalApi.getClientExpensePaymentMethods(eventId, expenseItemId!),
    enabled: !!eventId && !!expenseItemId,
  });
}

export function useClientEventDetail(eventId: string) {
  return useQuery<ClientEventDetail>({
    queryKey: ['portal', 'client', 'event-detail', eventId],
    queryFn: () => portalApi.getClientEventDetail(eventId),
    enabled: !!eventId,
  });
}

export function useClientEventGuests(eventId: string) {
  return useQuery<ClientGuest[]>({
    queryKey: ['portal', 'client', 'guests', eventId],
    queryFn: () => portalApi.getClientEventGuests(eventId),
    enabled: !!eventId,
  });
}

export function useCreateClientEventGuest(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: portalApi.PortalGuestInput) =>
      portalApi.createClientEventGuest(eventId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal', 'client', 'guests', eventId] });
      queryClient.invalidateQueries({ queryKey: ['portal', 'client', 'detail', eventId] });
    },
  });
}

export function useUpdateClientEventGuest(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      guestId,
      updates,
    }: {
      guestId: string;
      updates: Partial<portalApi.PortalGuestInput>;
    }) => portalApi.updateClientEventGuest(eventId, guestId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal', 'client', 'guests', eventId] });
    },
  });
}

export function useClientEventFloorPlans(eventId: string) {
  return useQuery<ClientFloorPlan[]>({
    queryKey: ['portal', 'client', 'floor-plans', eventId],
    queryFn: () => portalApi.getClientEventFloorPlans(eventId),
    enabled: !!eventId,
  });
}

export function useClientEventPhotos(eventId: string) {
  return useQuery<ClientPhoto[]>({
    queryKey: ['portal', 'client', 'photos', eventId],
    queryFn: () => portalApi.getClientEventPhotos(eventId),
    enabled: !!eventId,
  });
}

export function useClientEventTimeline(eventId: string) {
  return useQuery<ClientTimeline[]>({
    queryKey: ['portal', 'client', 'timeline', eventId],
    queryFn: () => portalApi.getClientEventTimeline(eventId),
    enabled: !!eventId,
  });
}

/**
 * The tasks assigned to the caller on the event — a client's through the
 * client portal, a vendor's through the vendor portal (`scope`).
 */
export function useClientTasks(eventId: string, enabled = true, scope: PortalTaskScope = 'client') {
  const userId = useAuth().user?.id ?? null;
  return useQuery<ClientTask[]>({
    queryKey: CLIENT_TASKS_KEY(scope, eventId, userId),
    queryFn: () => portalApi.listClientTasks(eventId, scope),
    enabled: !!eventId && enabled && userId !== null,
  });
}

/** The caller's tasks on every event they are on — the portal dashboards' "My tasks". */
export function useMyPortalTasks(enabled = true) {
  const userId = useAuth().user?.id ?? null;
  return useQuery<ClientTask[]>({
    queryKey: [...MY_TASKS_KEY, userId],
    queryFn: () => portalApi.listMyPortalTasks(),
    enabled: enabled && userId !== null,
  });
}

/**
 * The server answers only the caller's own tasks, so the cache is keyed by
 * who asked as well as the event: another client signing in on the same
 * browser must never be shown the first one's tasks and notes while theirs
 * load. Invalidating by the event's key alone still reaches every user's
 * entry, since it is the prefix.
 */
const CLIENT_TASKS_KEY = (scope: PortalTaskScope, eventId: string, userId?: string | null) =>
  userId === undefined
    ? ['portal', scope, 'tasks', eventId]
    : ['portal', scope, 'tasks', eventId, userId];

const MY_TASKS_KEY = ['portal', 'my-tasks'] as const;

/**
 * Fetch the caller's tasks across events again — for a link that names
 * something the cached copy (fresh for minutes) cannot yet know about.
 */
export function useRefreshMyPortalTasks() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: MY_TASKS_KEY });
}

/** After any change to a task: the event's list and the list across events. */
function refreshTasks(
  queryClient: ReturnType<typeof useQueryClient>,
  scope: PortalTaskScope,
  eventId: string,
) {
  void queryClient.invalidateQueries({ queryKey: CLIENT_TASKS_KEY(scope, eventId) });
  return queryClient.invalidateQueries({ queryKey: MY_TASKS_KEY });
}

export function useCreateClientTask(eventId: string, scope: PortalTaskScope = 'client') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateClientTaskInput) => portalApi.createClientTask(eventId, input, scope),
    onSettled: () => refreshTasks(queryClient, scope, eventId),
  });
}

export function useUpdateClientTask(eventId: string, scope: PortalTaskScope = 'client') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, input }: { taskId: string; input: UpdateClientTaskInput }) =>
      portalApi.updateClientTask(eventId, taskId, input, scope),
    onSettled: () => refreshTasks(queryClient, scope, eventId),
  });
}

export function useDeleteClientTask(eventId: string, scope: PortalTaskScope = 'client') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => portalApi.deleteClientTask(eventId, taskId, scope),
    onSettled: () => refreshTasks(queryClient, scope, eventId),
  });
}

/**
 * Ticks a task at once — in the event's list and the list across events — and
 * a failed save puts that one task back.
 */
export function useToggleClientTask(eventId: string, scope: PortalTaskScope = 'client') {
  const queryClient = useQueryClient();
  const userId = useAuth().user?.id ?? null;
  const keys = [CLIENT_TASKS_KEY(scope, eventId, userId), [...MY_TASKS_KEY, userId]];
  return useMutation({
    mutationFn: ({ taskId, isDone }: { taskId: string; isDone: boolean }) =>
      portalApi.toggleClientTask(eventId, taskId, isDone, scope),
    onMutate: async ({ taskId, isDone }) => {
      await Promise.all(keys.map((key) => queryClient.cancelQueries({ queryKey: key })));
      const before = keys
        .map((key) => queryClient.getQueryData<ClientTask[]>(key)?.find((t) => t.id === taskId))
        .find(Boolean);
      const set = (fields: Pick<ClientTask, 'isDone' | 'status'>) => {
        for (const key of keys) {
          queryClient.setQueryData<ClientTask[]>(key, (tasks) =>
            tasks?.map((t) => (t.id === taskId ? { ...t, ...fields } : t)),
          );
        }
      };
      set({
        isDone,
        status: isDone ? 'DONE' : before?.status === 'DONE' ? 'TODO' : (before?.status ?? 'TODO'),
      });
      return {
        restore: before ? () => set({ isDone: before.isDone, status: before.status }) : null,
      };
    },
    onError: (_err, _vars, context) => context?.restore?.(),
    onSettled: () => refreshTasks(queryClient, scope, eventId),
  });
}

export function useClientEventQuestionnaires(eventId: string) {
  return useQuery<ClientQuestionnaire[]>({
    queryKey: ['portal', 'client', 'questionnaires', eventId],
    queryFn: () => portalApi.getClientEventQuestionnaires(eventId),
    enabled: !!eventId,
  });
}

export function useUpdateClientApproval() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      expenseItemId,
      clientApproval,
    }: {
      expenseItemId: string;
      clientApproval: ClientApprovalStatus;
    }) => portalApi.updateClientApproval(expenseItemId, clientApproval),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal', 'client', 'expenses'] });
    },
  });
}

// ─── Vendor Portal ─────────────────────────────────────────────────────────

export function useVendorEvents() {
  return useQuery<VendorEvent[]>({
    queryKey: ['portal', 'vendor', 'events'],
    queryFn: () => portalApi.getVendorEvents(),
  });
}

export function useVendorEventExpenses(eventId: string) {
  return useQuery<VendorExpenseItem[]>({
    queryKey: ['portal', 'vendor', 'expenses', eventId],
    queryFn: () => portalApi.getVendorEventExpenses(eventId),
    enabled: !!eventId,
  });
}

export function useVendorEventItems(eventId: string, enabled = true) {
  // Keyed by user: an office member previewing one vendor and then another
  // (impersonation, no reload) must never be shown the first one's items.
  const userId = useAuth().user?.id ?? null;
  return useQuery({
    queryKey: ['portal', 'vendor', 'items', eventId, userId],
    queryFn: () => portalApi.getVendorEventItems(eventId),
    enabled: enabled && !!eventId && userId !== null,
  });
}

export function useVendorEventTimeline(eventId: string) {
  return useQuery<VendorTimelineEntry[]>({
    queryKey: ['portal', 'vendor', 'timeline', eventId],
    queryFn: () => portalApi.getVendorEventTimeline(eventId),
    enabled: !!eventId,
  });
}

export function useVendorEventFloorPlans(eventId: string, enabled = true) {
  return useQuery<VendorFloorPlan[]>({
    queryKey: ['portal', 'vendor', 'floor-plans', eventId],
    queryFn: () => portalApi.getVendorEventFloorPlans(eventId),
    enabled: enabled && !!eventId,
  });
}

export function useVendorProfile() {
  return useQuery<VendorProfile>({
    queryKey: ['portal', 'vendor', 'profile'],
    queryFn: () => portalApi.getVendorProfile(),
  });
}

export function useUpdateVendorProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      updates: Partial<Pick<VendorProfile, 'businessName' | 'phone' | 'email' | 'website'>>,
    ) => portalApi.updateVendorProfile(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal', 'vendor', 'profile'] });
    },
  });
}

export function useUploadVendorDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      ...doc
    }: {
      eventId: string;
      file: File;
      documentType: 'quote' | 'invoice';
      expenseItemId: string;
    }) => portalApi.uploadVendorDocument(eventId, doc),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal', 'vendor'] });
    },
  });
}
